import { createSlice} from '@reduxjs/toolkit';
import {
  createEmployer,
  createClientAlert,
  deleteClientAlert,
  getAlerts,
  getEmployers,
  getMetaData,
  getPaymentMetadata,
  patchClientAlert,
} from './meta-data-thunk';
import {
  CRMObjectTypeMetaData,
  CRMTypeMetaData,
  PaymentChannelMetaData,
  TimingRiskMetaData,
  Alerts,
  emptyAlerts,
  Employers,
  emptyEmployers,
  RuleParamsMetadata,
  VobSource,
  SubscriberRelation, EmploymentStatus, EducationLevel,
  WorkflowMetaData,
} from '../models/metaData';
import { getCRMMetaData } from './crm-meta-data-thunk';
import _ from 'lodash';

export interface MetaDataState {
  paymentChannel: PaymentChannelMetaData[],
  timingRisk: TimingRiskMetaData[],
  crmObjectType: CRMObjectTypeMetaData[],
  crmType: CRMTypeMetaData[],
  alerts: Alerts,
  employers: Employers,
  employmentStatus: EmploymentStatus[],
  ruleParams: RuleParamsMetadata[],
  vobSource: VobSource[],
  subscriberRelation: SubscriberRelation[]
  educationLevel: EducationLevel[]
  /** Workflows with nested workflowStatus → workflowSubStatus from encounter metaData API */
  workflow: WorkflowMetaData[]
  isLoading: {
    metadata: boolean
  },
  isError: {
    metadata: boolean
  }
}

const initialState: MetaDataState = {
  paymentChannel: [],
  timingRisk: [],
  crmObjectType: [],
  crmType:[],
  alerts: emptyAlerts,
  employers: emptyEmployers,
  employmentStatus: [],
  ruleParams: [],
  vobSource: [],
  subscriberRelation: [],
  educationLevel: [],
  workflow: [],
  isLoading: {
    metadata: false
  },
  isError: {
    metadata: false
  }
};

export const metaDataSlice = createSlice({
  name: "metaData",
  initialState,
  reducers: {
    setVobSource: (state, action) => {
      state.vobSource = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getMetaData.fulfilled, (state, action) => {
      state.paymentChannel = action.payload.paymentChannel;
      state.timingRisk = action.payload.timingRisk;
      state.subscriberRelation = action.payload.subscriberRelation
      state.employmentStatus = action.payload.employmentStatus
      state.educationLevel = action.payload.educationLevel
      state.workflow = Array.isArray(action.payload.workflow)
        ? action.payload.workflow
        : []
      state.isLoading.metadata = false
      state.isError.metadata = false
    });
    builder.addCase(getMetaData.pending, (state, action) => {
      state.isLoading.metadata = true
      state.isError.metadata = false
    });
    builder.addCase(getMetaData.rejected, (state, action) => {
      state.isLoading.metadata = false
      state.isError.metadata = true
    });
    builder.addCase(getCRMMetaData.fulfilled, (state, action) => {
      state.crmObjectType = action.payload.crmObjectType
      state.crmType = action.payload.crmType
    })
    builder.addCase(getAlerts.fulfilled, (state, action) => {
      state.alerts[+action.payload.clientId] = action.payload.alerts.entity
    })
    builder.addCase(getEmployers.fulfilled, (state, action) => {
      state.employers[+action.payload.clientId] = action.payload.employers.entity
    })
    builder.addCase(createEmployer.fulfilled, (state, action) => {
      if(state.employers.hasOwnProperty(`${action?.payload?.clientId}`)){
        state.employers[action.payload!.clientId] = _.sortBy([...state.employers[+action.payload!.clientId], action?.payload?.employer], (employer) => employer.employerName.toLowerCase())
      }else{
        state.employers[action.payload!.clientId] = [action.payload!.employer]
      }
    })

    builder.addCase(patchClientAlert.fulfilled, (state, action) => {
      if (!action.payload) return;
      const { clientId, updatedAlert } = action.payload;
      const clientAlerts = state.alerts[clientId];
      if (!clientAlerts || !updatedAlert) return;
      const typeKey = ['payer', 'prefix', 'group', 'employer'][updatedAlert.alert_type_id - 1] as keyof typeof clientAlerts;
      const list = clientAlerts[typeKey] as any[];
      const idx = list.findIndex(a => a.client_alert_id === updatedAlert.client_alert_id);
      if (idx !== -1) list[idx] = { ...list[idx], ...updatedAlert };
    })
    builder.addCase(createClientAlert.fulfilled, (_state, _action) => {
      // Intentionally skip optimistic update — the API response for createAlert
      // does not include joined fields (prefix, group_code, payor_id, etc.), so
      // pushing the incomplete object would cause downstream crashes in updateAlert.
      // The caller (EditAlertsModal) dispatches getAlerts() after save to refresh.
    })
    builder.addCase(deleteClientAlert.fulfilled, (state, action) => {
      if (!action.payload) return;
      const { clientId, clientAlertId } = action.payload;
      const clientAlerts = state.alerts[clientId];
      if (!clientAlerts) return;
      for (const typeKey of ['payer', 'prefix', 'group', 'employer'] as const) {
        const list = clientAlerts[typeKey] as any[];
        if (!Array.isArray(list)) continue;
        const idx = list.findIndex(a => a.client_alert_id === clientAlertId);
        if (idx !== -1) { list.splice(idx, 1); break; }
      }
    })
    builder.addCase(getPaymentMetadata.fulfilled, (state, action) => {
      Object.keys(action.payload).forEach((metadataKey) => {
        state[metadataKey as keyof typeof state] = action.payload[metadataKey]
      })
    })
  },
});

export const { setVobSource } = metaDataSlice.actions;
export default metaDataSlice.reducer;